import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormCheckbox, FormRadioGroup, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import {
  type CalculateMonthlyContributionParams,
  calculateMonthlyContribution,
  calculateMonthlyContributionWithFee,
  calculateMonthlyContributionWithoutFee,
} from "Charts/Utils/CommonChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type HousingOtherGoalFormType, getHousingOtherFormSchema, minDate } from "Forms/Schemas/Goals/housingOtherFormSchema";
import { toRadioOptions } from "Forms/fieldOptions";
import {
  getGetAnalysisGoalsQueryKey,
  getGetHousingOtherGoalQueryKey,
  useCreateHousingOtherGoal,
  useUpdateHousingOtherGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  GoalOverviewSubType,
  type HousingOtherGoalDto,
  HousingOtherGoalDtoPriorityLevel,
} from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_INFLATION_RATE, INTEREST_RATE_AGGRESIVE, INTEREST_RATE_CONSERVATIVE } from "Infrastructure/Constants/financialConstants";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { differenceInMonths, format, isBefore, startOfDay } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ModelingAccordion } from "./ModelingAccordion";
import { SavingsChart } from "./SavingsChart";

type Props = {
  readonly onClose: () => void;
  readonly onSubTypeChange: (goalSubType: GoalOverviewSubType) => void;
  readonly editGoal?: HousingOtherGoalDto;
  readonly goalId?: string;
};

export const HousingOtherGoalForm = forwardRef<{}, Props>(({ onSubTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;
  const methods = useForm<HousingOtherGoalFormType>({
    resolver: yupResolver(getHousingOtherFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          includeInflation: editGoal.modeling.inflation?.enabled,
          includeFee: editGoal.modeling.fee?.enabled,
          inflationRate: editGoal.modeling.inflation?.rate ?? 0,
          feeRate: editGoal.modeling.fee?.rate ?? 0,
          goalSubType: GoalOverviewSubType.housing_other,
          name: editGoal.name,
          targetDate: new Date(editGoal.data.targetDate as string),
          targetAmount: editGoal.data.targetAmount?.value.toString() as any,
          monthlyContribution: editGoal.modeling.investmentAmount?.value.toString() as any,
          monthlyContributionWithFee: editGoal.modeling.investmentAmount?.value.toString() as any,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          note: editGoal.note || "",
          priority: editGoal.priorityLevel === HousingOtherGoalDtoPriorityLevel.high,
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeFee: true,
          inflationRate: DEFAULT_INFLATION_RATE,
          feeRate: DEFAULT_INFLATION_RATE,
          goalSubType: GoalOverviewSubType.housing_other,
          name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
          referenceStartDate: new Date(),
        },
  });

  const { control, setValue, watch, clearErrors, handleSubmit } = methods;

  const targetDate = watch("targetDate");
  const targetAmount = watch("targetAmount");
  const interestRate = watch("interestRate");
  const includeInflation = watch("includeInflation");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const inflationRate = watch("inflationRate");
  const monthlyContribution = watch("monthlyContribution");
  const referenceStartDate = watch("referenceStartDate");

  const investmentLength = differenceInMonths(targetDate, new Date());
  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const shouldRenderChart =
    targetDate && !isInvalidTargetDate && interestRate >= 0 && inflationRate >= 0 && monthlyContribution && targetAmount;

  useEffect(() => {
    if (editGoal) {
      return;
    }

    if (investmentLength && investmentLength > 120) {
      setValue("interestRate", INTEREST_RATE_AGGRESIVE.toString() as any);
    } else {
      setValue("interestRate", INTEREST_RATE_CONSERVATIVE.toString() as any);
    }
  }, [setValue, investmentLength, editGoal]);

  useEffect(() => {
    const monthlyContribution = calculateMonthlyContribution({
      targetAmount,
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      interestRate,
      inflationRate: includeInflation ? Number(inflationRate) : 0,
    });
    const newContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, feeRate);
    setValue("monthlyContributionWithFee", newContributionWithFee.toString() as any);
  }, [feeRate, inflationRate, interestRate, targetAmount, targetDate, includeInflation, setValue]);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(editGoal ? onEditGoal : onCreateGoal)();
    },
  }));

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
    onChangeTargetDate();
  }, [targetDate, isInvalidTargetDate, clearErrors]);

  const onChangeTargetDate = () => {
    onChange({
      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
      targetAmount,
      inflationRate,
      interestRate,
    });
  };

  const queryClient = useQueryClient();

  const { mutate: createHousingOtherGoal } = useCreateHousingOtherGoal();

  const { mutate: updateHousingOtherGoal } = useUpdateHousingOtherGoal();

  const onChange = ({ inflationRate, interestRate, targetAmount, durationInMonths }: CalculateMonthlyContributionParams) => {
    if (interestRate && targetAmount && durationInMonths) {
      const monthlyContribution = calculateMonthlyContribution({
        targetAmount,
        durationInMonths,
        interestRate,
        inflationRate: includeInflation ? Number(inflationRate) : 0,
      });
      setValue("monthlyContribution", monthlyContribution.toString() as any);
    }
  };

  const onCreateGoal = (data: HousingOtherGoalFormType) => {
    createHousingOtherGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingOtherGoalDtoPriorityLevel.high : HousingOtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: HousingOtherGoalFormType) => {
    updateHousingOtherGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingOtherGoalDtoPriorityLevel.high : HousingOtherGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            targetAmount: {
              value: data.targetAmount,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            investmentAmount: {
              value: data.monthlyContributionWithFee,
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: data.inflationRate,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: data.feeRate,
            },
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetHousingOtherGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Housing)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <FormRadioGroup
                  control={control}
                  name="goalSubType"
                  label={t(Resources.AnalysisDetail.Goals.WhatAreYouPlanning)}
                  options={toRadioOptions([
                    {
                      code: GoalOverviewSubType.housing_property,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
                    },
                    {
                      code: GoalOverviewSubType.housing_mortgage,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
                    },
                    {
                      code: GoalOverviewSubType.housing_other,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
                    },
                  ])}
                  defaultValue={GoalOverviewSubType.housing_property}
                  columns={2}
                  disabled={!!editGoal || readOnly}
                  onChange={(goalSubType) => onSubTypeChange(goalSubType as GoalOverviewSubType)}
                />
              </Grid>
              <Grid size={6}>
                <FormDatePicker
                  control={control}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  required
                  minDate={minDate}
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={6}>
                <FormNumberField
                  control={control}
                  name="targetAmount"
                  label={t(Resources.AnalysisDetail.Goals.DrawAmount)}
                  required
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onChange={(targetAmount) =>
                    onChange({
                      durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                      targetAmount: Number(targetAmount),
                      inflationRate,
                      interestRate,
                    })
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      required
                      scale={2}
                      thousandsSeparator=" "
                      radix=","
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      onChange={(newContributionWithFee) => {
                        setValue("monthlyContribution", calculateMonthlyContributionWithoutFee(Number(newContributionWithFee), feeRate));
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <FormNumberField
                      control={control}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      required
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=" "
                      radix=","
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) => {
                        if (
                          (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                          (!includeInflation && Number(newInterestRate) <= 0)
                        ) {
                          setValue("interestRate", interestRate);
                          return;
                        }
                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate,
                          interestRate: Number(newInterestRate),
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onChange={(newInflationRate) => {
                        if (Number(newInflationRate) === Number(interestRate)) {
                          setValue("inflationRate", inflationRate);
                          return;
                        }
                        onChange({
                          durationInMonths: targetDate ? differenceInMonths(targetDate, new Date()) : 0,
                          targetAmount,
                          inflationRate: Number(newInflationRate),
                          interestRate,
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        setValue("feeRate", newFeeRate);
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=" "
                      radix=","
                      unit="%"
                      disabled={!includeFee || readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <SavingsChart
                    referenceStartDate={referenceStartDate}
                    targetDate={targetDate}
                    targetAmount={targetAmount}
                    interestRate={interestRate ? Number(interestRate) : 0}
                    monthlyContributionAmount={Number(monthlyContribution)}
                    inflationRate={inflationRate}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
